import type { Comment, Member, Photo, Place, ServerEvent, TripState, Vote } from '@map/shared';

interface Identified {
  readonly id: number;
}

/**
 * Insere ou remplace par identifiant, en conservant le rang existant.
 *
 * C'est ici que se joue l'idempotence : le serveur peut rediffuser un
 * evenement, et le client emet localement le meme evenement juste apres sa
 * propre requete REST pour ne pas attendre l'aller-retour WebSocket. Recevoir
 * deux fois la meme creation ne doit donc rien dupliquer.
 */
function upsertById<T extends Identified>(list: readonly T[], entry: T): T[] {
  const index = list.findIndex((item) => item.id === entry.id);
  if (index === -1) return [...list, entry];
  const next = [...list];
  next[index] = entry;
  return next;
}

/** Renvoie la MEME reference si rien n'a ete retire, pour eviter un rendu inutile. */
function withoutId<T extends Identified>(list: readonly T[], id: number): readonly T[] {
  const next = list.filter((item) => item.id !== id);
  return next.length === list.length ? list : next;
}

function withoutPlaceId<T extends { readonly placeId: number }>(
  list: readonly T[],
  placeId: number,
): readonly T[] {
  const next = list.filter((item) => item.placeId !== placeId);
  return next.length === list.length ? list : next;
}

function upsertVote(votes: readonly Vote[], vote: Vote): readonly Vote[] {
  const index = votes.findIndex(
    (item) => item.placeId === vote.placeId && item.memberId === vote.memberId,
  );
  if (index === -1) return [...votes, vote];
  if (votes[index]?.value === vote.value) return votes;
  const next = [...votes];
  next[index] = vote;
  return next;
}

function removeVote(votes: readonly Vote[], placeId: number, memberId: number): readonly Vote[] {
  const next = votes.filter((item) => !(item.placeId === placeId && item.memberId === memberId));
  return next.length === votes.length ? votes : next;
}

/**
 * Reconcilie l'itineraire complet renvoye par le serveur.
 *
 * L'evenement ne porte que les etapes retenues. Un lieu qui etait dans
 * l'itineraire et n'y figure plus a donc ete retrograde : sans ce traitement
 * il resterait affiche dans les deux panneaux a la fois.
 */
function reorderPlaces(places: readonly Place[], ordered: readonly Place[]): Place[] {
  const byId = new Map<number, Place>(ordered.map((entry) => [entry.id, entry]));
  const known = new Set<number>(places.map((entry) => entry.id));

  const merged = places.map((current) => {
    const replacement = byId.get(current.id);
    if (replacement !== undefined) return replacement;
    if (current.status !== 'itinerary') return current;
    return { ...current, status: 'pool' as const, orderIndex: null };
  });

  for (const entry of ordered) {
    if (!known.has(entry.id)) merged.push(entry);
  }
  return merged;
}

function upsertMember(members: readonly Member[], member: Member): Member[] {
  return upsertById(members, member);
}

function upsertComment(comments: readonly Comment[], comment: Comment): Comment[] {
  return upsertById(comments, comment);
}

function upsertPhoto(photos: readonly Photo[], photo: Photo): Photo[] {
  return upsertById(photos, photo);
}

/**
 * Applique un evenement serveur a l'etat du voyage.
 *
 * Fonction PURE : aucun acces reseau, aucune mutation de l'argument. C'est
 * ce qui permet de la tester exhaustivement sans monter ni serveur ni React,
 * et de rejouer une salve d'evenements pour verifier la convergence.
 */
export function applyEvent(state: TripState, event: ServerEvent): TripState {
  switch (event.type) {
    case 'member:joined':
      return { ...state, members: upsertMember(state.members, event.member) };

    case 'place:created':
    case 'place:updated':
      return { ...state, places: upsertById(state.places, event.place) };

    case 'place:deleted': {
      const places = withoutId(state.places, event.placeId);
      const votes = withoutPlaceId(state.votes, event.placeId);
      const comments = withoutPlaceId(state.comments, event.placeId);
      const photos = withoutPlaceId(state.photos, event.placeId);
      if (
        places === state.places &&
        votes === state.votes &&
        comments === state.comments &&
        photos === state.photos
      ) {
        return state;
      }
      return { ...state, places: [...places], votes: [...votes], comments: [...comments], photos: [...photos] };
    }

    case 'vote:changed': {
      const votes = upsertVote(state.votes, event.vote);
      return votes === state.votes ? state : { ...state, votes: [...votes] };
    }

    case 'vote:removed': {
      const votes = removeVote(state.votes, event.placeId, event.memberId);
      return votes === state.votes ? state : { ...state, votes: [...votes] };
    }

    case 'comment:added':
      return { ...state, comments: upsertComment(state.comments, event.comment) };

    case 'comment:deleted': {
      const comments = withoutId(state.comments, event.commentId);
      return comments === state.comments ? state : { ...state, comments: [...comments] };
    }

    case 'photo:added':
      return { ...state, photos: upsertPhoto(state.photos, event.photo) };

    case 'photo:deleted': {
      const photos = withoutId(state.photos, event.photoId);
      return photos === state.photos ? state : { ...state, photos: [...photos] };
    }

    case 'itinerary:reordered':
      return { ...state, places: reorderPlaces(state.places, event.places) };
  }
}
